<template>
  <div id="app" :style="{ height: this.height }" :data-theme="currentTheme">
    <!-- 后台管理页面（有左侧菜单） -->
    <el-row v-if="showMenu" class="admin-layout">
      <el-col ref="leftmenu" class="sidebar" :span="3" :style="{ width: menuwith }">
        <div class="sidebar-header">
          <div class="logo">
            <i class="el-icon-s-platform"></i>
            <span v-show="!isCollapse">管理系统</span>
          </div>
          <div class="menu-expend" ref="menutop">
            <i :class="expendClass" @click="expendHandle"></i>
          </div>
        </div>
        <el-menu router :default-active="activeIndex" :collapse="isCollapse" :collapse-transition="false" ref="menu"
          :unique-opened="true" :default-openeds="openedMenus" :style="{ height: 'calc(' + this.height + ' - 60px)' }"
          class="el-menu-vertical-demo" @select="handleSelect">
          <template v-for="(item, index) in menuList">
            <el-menu-item v-if="item.children.length === 0" :key="index" :index="item.index">
              <i :class="item.class"></i>
              <span slot="title">{{ item.title }}</span>
            </el-menu-item>
            <el-submenu v-if="item.children.length > 0" :index="item.index" :key="index">
              <template slot="title">
                <i :class="item.class"></i>
                <span>{{ item.title }}</span>
              </template>
              <el-menu-item v-for="(sub, index) in item.children" :key="index" :index="sub.index">
                <i :class="sub.class"></i>
                <span slot="title">{{ sub.title }}</span>
              </el-menu-item>
            </el-submenu>
          </template>
        </el-menu>
      </el-col>
      <el-col ref="main" :span="21" class="main-content" :style="{ width: breadcrumbWidth, height: this.height }">
        <!-- 顶部导航栏 -->
        <div class="top-header">
          <div class="breadcrumb-container">
            <el-breadcrumb separator="/">
              <el-breadcrumb-item v-for="(item, index) in breadcrumb" :key="index" :to="{ path: item.path }">
                {{ item.title }}
              </el-breadcrumb-item>
            </el-breadcrumb>
          </div>
          <div class="header-actions">
            <theme-toggle></theme-toggle>
          </div>
        </div>
        <!-- 页面内容 -->
        <div class="page-content">
          <router-view></router-view>
        </div>
      </el-col>
    </el-row>
    <!-- 认证页面（登录/注册/重置密码，独立页面） -->
    <el-row v-else-if="isAuthPage">
      <router-view></router-view>
    </el-row>
    <!-- 前台业务页面（无左侧菜单） -->
    <el-row v-else>
      <router-view name="class"></router-view>
    </el-row>
  </div>
</template>

<script>
import Vue from 'vue'
import api from './utils/api'
import auth from './utils/auth'

import ThemeToggle from './components/common/ThemeToggle'

export default {
  name: 'App',
  components: {
    ThemeToggle
  },
  data () {
    return {
      menuList: [],
      isCollapse: false,
      expendClass: 'el-icon-s-fold',
      menuwith: '12.5%',
      breadcrumbWidth: '87.5%',
      breadcrumb: [],
      height: '600px',
      openedMenus: [], // 存储需要展开的菜单index
      // 新增：初始化状态和用户信息
      appInitialized: false,
      userInfo: null,
      defaultPath: null,
      initialized: false,
      currentTheme: 'light'
    }
  },
  computed: {
    // 根据路由meta.pageType判断是否显示菜单
    // 当用户同时拥有admin和user角色时，也显示左侧菜单
    showMenu () {
      const pageType = this.$route.meta && this.$route.meta.pageType
      // 如果是后台页面，显示菜单
      if (pageType === 'admin') {
        return true
      }

      return false
    },
    // 判断是否是认证页面（登录/注册/重置密码）
    isAuthPage () {
      const pageType = this.$route.meta && this.$route.meta.pageType
      return pageType === 'auth'
    },
    activeIndex () {
      return this.$route.path
    }
  },
  created () {
    this.height = window.innerHeight + 'px'
    // 主题由 main.js 中的 initTheme() 统一初始化
    // 这里只需读取当前实际状态
    this.currentTheme = document.documentElement.getAttribute('data-theme') || 'light'
  },
  async mounted () {
    await this.initApp()
    // 监听主题变化
    this.$nextTick(() => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
            this.currentTheme = document.documentElement.getAttribute('data-theme') || 'light'
          }
        })
      })
      observer.observe(document.documentElement, { attributes: true })
    })
  },
  methods: {
    async initApp () {
      // 认证页面不需要初始化用户信息和菜单
      if (this.isAuthPage) {
        this.setAppInitialized()
        return
      }

      // 检查登录状态
      if (!auth.isLoggedIn()) {
        this.setAppInitialized()
        return
      }

      try {
        // 检查路由守卫是否已经获取了用户信息
        if (Vue.prototype.$userInfo) {
          this.userInfo = Vue.prototype.$userInfo
        } else {
          // 获取用户信息
          await this.getUserInfo()
        }

        // 检查路由守卫是否已经获取了菜单
        if (Vue.prototype.$menuList) {
          this.menuList = Vue.prototype.$menuList
        } else {
          // 获取菜单
          await this.initMenu()
        }

        // 检查路由守卫是否已经获取了默认首页
        if (Vue.prototype.$defaultPath) {
          this.defaultPath = Vue.prototype.$defaultPath
        } else {
          // 获取默认首页
          await this.fetchDefaultPath()
        }

        // 更新面包屑
        this.updateBreadcrumb()
      } catch (error) {
        console.error('初始化应用失败:', error)
      } finally {
        // 无论成功失败，都要标记初始化完成
        this.setAppInitialized()
      }
    },

    // 设置应用初始化完成状态，并共享到全局
    setAppInitialized () {
      this.appInitialized = true
      // 共享到 Vue 原型，供路由守卫使用
      Vue.prototype.$appInitialized = true
      Vue.prototype.$userInfo = this.userInfo
      Vue.prototype.$defaultPath = this.defaultPath
    },

    // 获取用户信息
    async getUserInfo () {
      try {
        const response = await this.$axios.post(api.getUserInfo)
        if (response.data && response.data.result) {
          this.userInfo = response.data.result
          Vue.prototype.$userInfo = this.userInfo
        }
      } catch (error) {
        console.error('获取用户信息失败:', error)
      }
    },

    async initMenu () {
      try {
        const response = await this.$axios.get(api.getUserMenus)
        if (response.data && response.data.result.length > 0) {
          this.menuList = response.data.result
          Vue.prototype.$menuList = this.menuList
        }
      } catch (error) {
        console.error('获取菜单失败:', error)
      }
    },

    // 获取用户默认首页
    async fetchDefaultPath () {
      try {
        const response = await this.$axios.get(api.getDefaultMenu)
        if (response.data && response.data.result && response.data.result.index) {
          this.defaultPath = response.data.result.index
          Vue.prototype.$defaultPath = this.defaultPath
        } else if (this.menuList.length > 0) {
          // 如果没有默认首页，使用第一个菜单
          const firstMenu = this.menuList[0]
          this.defaultPath = firstMenu.children && firstMenu.children.length > 0
            ? firstMenu.children[0].index
            : firstMenu.index
          Vue.prototype.$defaultPath = this.defaultPath
        }
      } catch (error) {
        console.error('获取默认首页失败:', error)
        // 如果获取失败，尝试使用第一个菜单
        if (this.menuList.length > 0) {
          const firstMenu = this.menuList[0]
          this.defaultPath = firstMenu.children && firstMenu.children.length > 0
            ? firstMenu.children[0].index
            : firstMenu.index
          Vue.prototype.$defaultPath = this.defaultPath
        }
      }
    },

    handleSelect (key, keyPath) {
      this.updateBreadcrumb()
    },

    // 根据当前路由更新面包屑
    updateBreadcrumb () {
      const currentPath = this.$route.path
      const routeMeta = this.$route.meta

      // 初始化面包屑
      this.breadcrumb = [{ title: '首页', path: '/' }]

      // 从菜单树中查找当前路径
      const menuPath = this.findMenuPath(this.menuList, currentPath)
      if (menuPath && menuPath.length > 0) {
        this.breadcrumb.push(...menuPath)
      } else if (routeMeta && routeMeta.title) {
        // 如果菜单中没有找到，使用路由meta中的title
        this.breadcrumb.push({ title: routeMeta.title, path: currentPath })
      }
    },

    // 在菜单树中查找路径
    findMenuPath (menus, targetPath, path = []) {
      for (const menu of menus) {
        const currentPath = [...path, { title: menu.title, path: menu.index }]

        if (menu.index === targetPath) {
          return currentPath
        }

        if (menu.children && menu.children.length > 0) {
          const result = this.findMenuPath(menu.children, targetPath, currentPath)
          if (result) {
            return result
          }
        }
      }
      return null
    },

    // 根据当前路由查找父菜单index并更新展开状态
    updateOpenedMenus () {
      const currentPath = this.$route.path
      const parentIndex = this.findParentMenuIndex(this.menuList, currentPath)
      if (parentIndex) {
        this.openedMenus = [parentIndex]
      } else {
        this.openedMenus = []
      }
    },

    // 查找当前路由对应的父菜单index
    findParentMenuIndex (menus, targetPath) {
      for (const menu of menus) {
        // 如果当前菜单就是目标，返回null（顶级菜单无需展开）
        if (menu.index === targetPath) {
          return null
        }
        // 检查子菜单
        if (menu.children && menu.children.length > 0) {
          for (const child of menu.children) {
            if (child.index === targetPath) {
              return menu.index // 返回父菜单的index
            }
          }
        }
      }
      return null
    },

    expendHandle () {
      var _this = this
      _this.isCollapse = !this.isCollapse
      this.$nextTick(function () {
        if (!_this.isCollapse) {
          _this.menuwith = '12.5%'
          _this.expendClass = 'el-icon-s-fold'
          _this.breadcrumbWidth = '87.5%'
        } else {
          _this.menuwith = _this.$refs.menu.$el.clientWidth + 'px'
          _this.expendClass = 'el-icon-s-unfold'
          _this.breadcrumbWidth = (document.body.clientWidth - _this.$refs.menu.$el.clientWidth - 10) + 'px'
        }
      })
    }
  },
  watch: {
    '$route': {
      handler (to, from) {
        // 当从认证页面跳转到非认证页面时，需要初始化数据
        const toPageType = to.meta && to.meta.pageType
        const fromPageType = from && from.meta && from.meta.pageType

        if (fromPageType === 'auth' && toPageType !== 'auth' && auth.isLoggedIn()) {
          // 从登录页跳转到业务页面，检查并获取数据
          if (!this.userInfo && Vue.prototype.$userInfo) {
            this.userInfo = Vue.prototype.$userInfo
          }
          if (!this.menuList.length && Vue.prototype.$menuList) {
            this.menuList = Vue.prototype.$menuList
          }
          if (!this.defaultPath && Vue.prototype.$defaultPath) {
            this.defaultPath = Vue.prototype.$defaultPath
          }
        }

        // 路由变化时更新面包屑
        this.updateBreadcrumb()

        // 更新菜单展开状态
        this.updateOpenedMenus()

        // 菜单宽度调整
        this.$nextTick(() => {
          if (this.$refs.menu && this.showMenu) {
            this.menuwith = this.$refs.menu.$el.clientWidth
            this.breadcrumbWidth = document.body.clientWidth - this.menuwith
          }
        })
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss">
@import './styles/variables.scss';
@import './styles/common.scss';

// 全局滚动条样式
::-webkit-scrollbar-track-piece {
  background-color: $bg-base;
  border-radius: 4px;
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-thumb {
  background-color: $border-base;
  background-clip: padding-box;
  min-height: 28px;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background-color: $border-dark;
}

body {
  margin: 0;
  overflow-y: hidden;
  padding-right: 0 !important;
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif;
}

#app {
  background: var(--bg-base);
}

// 后台布局样式
.admin-layout {
  overflow-y: hidden;

  .sidebar {
    background: linear-gradient(180deg, var(--sidebar-bg-start) 0%, var(--sidebar-bg-end) 100%);
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
    transition: width 0.3s;

    .sidebar-header {
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 16px;
      background: rgba(0, 0, 0, 0.15);
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);

      .logo {
        display: flex;
        align-items: center;
        gap: 10px;
        color: #fff;
        font-size: 18px;
        font-weight: 600;

        i {
          font-size: 24px;
          color: #409EFF;
        }
      }

      .menu-expend {
        color: #ffd04b;
        cursor: pointer;
        font-size: 16px;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.3s;

        &:hover {
          background: rgba(255, 255, 255, 0.1);
        }
      }
    }

    .el-menu {
      border-right: none;
      background: transparent;

      .el-menu-item,
      .el-submenu__title {
        color: #bfcbd9;
        height: 50px;
        line-height: 50px;

        i {
          color: #909399;
          margin-right: 10px;
        }

        &:hover {
          background: rgba(64, 158, 255, 0.1) !important;
          color: #fff;

          i {
            color: #fff;
          }
        }
      }

      .el-menu-item.is-active {
        background: rgba(64, 158, 255, 0.15) !important;
        color: #409EFF;
        border-right: 3px solid #409EFF;

        i {
          color: #409EFF;
        }
      }

      .el-submenu.is-opened>.el-submenu__title {
        color: #fff;

        i {
          color: #ffd04b;
        }
      }
    }
  }

  .main-content {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: width 0.3s;

    .top-header {
      height: 60px;
      background: var(--bg-white);
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 24px;
      box-shadow: var(--shadow-light);
      border-bottom: 1px solid var(--border-lighter);
      z-index: 10;

      .breadcrumb-container {
        .el-breadcrumb {
          font-size: 14px;

          .el-breadcrumb__inner {
            color: var(--text-regular);

            &.is-link {
              color: $primary-color;
              font-weight: 500;

              &:hover {
                color: $primary-light;
              }
            }
          }

          .el-breadcrumb__separator {
            color: var(--text-placeholder);
          }
        }
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: 16px;
      }
    }

    .page-content {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      background: var(--bg-base);
    }
  }
}

// 深色模式适配
[data-theme='dark'] {
  .admin-layout {
    .sidebar {
      box-shadow: 2px 0 12px rgba(0, 0, 0, 0.4);

      .el-menu {
        .el-menu-item,
        .el-submenu__title {
          color: rgba(255, 255, 255, 0.65);

          i {
            color: rgba(255, 255, 255, 0.4);
          }

          &:hover {
            background: rgba(64, 158, 255, 0.15) !important;
            color: #fff;

            i {
              color: rgba(255, 255, 255, 0.9);
            }
          }
        }

        .el-menu-item.is-active {
          background: rgba(64, 158, 255, 0.2) !important;
          color: $primary-color;

          i {
            color: $primary-color;
          }
        }

        .el-submenu.is-opened > .el-submenu__title {
          color: rgba(255, 255, 255, 0.9);
        }
      }
    }

    .main-content {
      .top-header {
        box-shadow: var(--shadow-base);
      }
    }
  }
}

// 响应式适配
@media (max-width: 768px) {
  .admin-layout {
    .sidebar {
      position: fixed;
      z-index: 1000;
      height: 100vh;
      transform: translateX(-100%);

      &.is-open {
        transform: translateX(0);
      }
    }

    .main-content {
      width: 100% !important;

      .top-header {
        padding: 0 16px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue